import { ReactNode, createContext, useContext, useEffect, useMemo } from 'react';

import { BarRaiserEvaluationService } from '../services/BarRaiserEvaluationService';
import { useContentEditorService } from './ContentEditorServiceContext';
import { useMplServiceClient } from './MplServiceClientContext';

const BarRaiserEvaluationServiceContext = createContext<BarRaiserEvaluationService | undefined>(
  undefined,
);

export { BarRaiserEvaluationServiceContext };

export function BarRaiserEvaluationServiceProvider({ children }: { children: ReactNode }) {
  const mpl = useMplServiceClient();
  const contentEditorService = useContentEditorService();
  const service = useMemo(
    () => new BarRaiserEvaluationService(mpl, contentEditorService),
    [mpl, contentEditorService],
  );

  useEffect(() => {
    return () => {
      service.close();
    };
  }, [service]);

  return (
    <BarRaiserEvaluationServiceContext.Provider value={service}>
      {children}
    </BarRaiserEvaluationServiceContext.Provider>
  );
}

export function useBarRaiserEvaluationService(): BarRaiserEvaluationService {
  const context = useContext(BarRaiserEvaluationServiceContext);
  if (!context) {
    throw new Error(
      'useBarRaiserEvaluationService must be used within BarRaiserEvaluationServiceProvider',
    );
  }
  return context;
}

/**
 * Returns the bar raiser evaluation service, or undefined when no provider is
 * present. Use this where the evaluation panel is optional (e.g. the edit page,
 * which no longer hosts it) to gate behaviour without throwing.
 */
export function useOptionalBarRaiserEvaluationService(): BarRaiserEvaluationService | undefined {
  return useContext(BarRaiserEvaluationServiceContext);
}
